import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Edit, Search, Tag as TagIcon, ShieldAlert } from 'lucide-react'
import { contentApi, type SensitiveAction, type SensitiveWordItem } from '../../lib/api'

const ACTIONS: { value: SensitiveAction; label: string; color: string; tip: string }[] = [
  { value: 'block', label: '屏蔽', color: 'bg-red-100 text-red-700', tip: '替换为 * 字符' },
  { value: 'replace', label: '替换', color: 'bg-orange-100 text-orange-700', tip: '替换为 replacement' },
  { value: 'review', label: '审核', color: 'bg-yellow-100 text-yellow-700', tip: '只记录审核日志，文本不变' },
]

const SensitiveWords = () => {
  const [items, setItems] = useState<SensitiveWordItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [editing, setEditing] = useState<SensitiveWordItem | null>(null)
  const [form, setForm] = useState<{ word: string; category: SensitiveAction; replacement: string; is_active: 0 | 1 }>({
    word: '', category: 'block', replacement: '', is_active: 1,
  })
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const r = await contentApi.adminListSensitiveWords({ keyword: keyword || undefined, page, page_size: pageSize })
      setItems(r.items || [])
      setTotal(r.total || 0)
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '加载失败' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [page])

  const onSubmit = async () => {
    const word = form.word.trim()
    if (!word) return setToast({ kind: 'error', msg: '请输入敏感词' })
    if (form.category === 'replace' && !form.replacement.trim()) {
      return setToast({ kind: 'error', msg: '替换策略必须填写替换内容' })
    }
    try {
      if (editing) {
        await contentApi.adminUpdateSensitiveWord(editing.id, {
          word,
          category: form.category,
          replacement: form.replacement || undefined,
          is_active: form.is_active,
        })
        setToast({ kind: 'success', msg: '已更新' })
      } else {
        await contentApi.adminCreateSensitiveWord({
          word,
          category: form.category,
          replacement: form.replacement || undefined,
        })
        setToast({ kind: 'success', msg: '已新增' })
      }
      setEditing(null)
      setForm({ word: '', category: 'block', replacement: '', is_active: 1 })
      load()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '保存失败' })
    }
  }

  const onEdit = (w: SensitiveWordItem) => {
    setEditing(w)
    setForm({
      word: w.word,
      category: w.category,
      replacement: w.replacement || '',
      is_active: (w.is_active ? 1 : 0) as 0 | 1,
    })
  }

  const onDelete = async (id: string) => {
    if (!confirm('确认删除该敏感词？')) return
    try {
      await contentApi.adminDeleteSensitiveWord(id)
      setToast({ kind: 'success', msg: '已删除' })
      load()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '删除失败' })
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {toast && (
        <div className={'fixed top-20 right-6 z-50 px-4 py-2 rounded shadow-lg text-sm ' + (toast.kind === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200')}>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-gray-600">×</button>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-orange-500" />敏感词管理
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">用于评论 / 留言内容的自动过滤</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">敏感词</label>
          <input
            value={form.word}
            onChange={e => setForm(f => ({ ...f, word: e.target.value }))}
            placeholder="请输入敏感词"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">处理策略</label>
          <select
            value={form.category}
            onChange={e => setForm(f => ({ ...f, category: e.target.value as SensitiveAction }))}
            className="w-full border rounded px-3 py-2 text-sm"
          >
            {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}（{a.tip}）</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">替换内容（仅 replace）</label>
          <input
            value={form.replacement}
            onChange={e => setForm(f => ({ ...f, replacement: e.target.value }))}
            placeholder="可留空"
            disabled={form.category !== 'replace'}
            className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
          />
        </div>
        <div className="flex gap-2">
          {editing && (
            <button onClick={() => { setEditing(null); setForm({ word: '', category: 'block', replacement: '', is_active: 1 }) }} className="px-3 py-2 text-sm border rounded text-gray-600 hover:bg-gray-50">
              取消
            </button>
          )}
          <button onClick={onSubmit} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700">
            {editing ? <><Edit className="w-3 h-3" />保存</> : <><Plus className="w-3 h-3" />{editing ? '保存' : '新增'}</>}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load() } }}
              placeholder="搜索敏感词"
              className="border rounded px-2 py-1 text-sm"
            />
            <button onClick={() => { setPage(1); load() }} className="px-2 py-1 text-xs border rounded hover:bg-gray-50">搜索</button>
          </div>
          <span className="text-xs text-gray-400">共 {total} 条</span>
        </div>

        {loading ? (
          <div className="text-center text-sm text-gray-400 py-6">加载中…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-6">暂无敏感词</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-2">敏感词</th>
                  <th className="text-left py-2 px-2">策略</th>
                  <th className="text-left py-2 px-2">替换内容</th>
                  <th className="text-left py-2 px-2">状态</th>
                  <th className="text-left py-2 px-2">创建时间</th>
                  <th className="text-right py-2 px-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(w => {
                  const a = ACTIONS.find(x => x.value === w.category) || ACTIONS[0]
                  return (
                    <tr key={w.id} className="border-b last:border-b-0 hover:bg-gray-50">
                      <td className="py-2 px-2 font-medium">{w.word}</td>
                      <td className="py-2 px-2"><span className={'inline-block text-xs px-2 py-0.5 rounded ' + a.color}>{a.label}</span></td>
                      <td className="py-2 px-2 text-gray-600">{w.replacement || '-'}</td>
                      <td className="py-2 px-2 text-xs">
                        {w.is_active ? <span className="text-green-600">启用</span> : <span className="text-gray-400">停用</span>}
                      </td>
                      <td className="py-2 px-2 text-xs text-gray-500">{w.created_at?.slice(0, 16).replace('T', ' ') || '-'}</td>
                      <td className="py-2 px-2 text-right space-x-2">
                        <button onClick={() => onEdit(w)} className="text-blue-600 hover:underline text-xs">编辑</button>
                        <button onClick={() => onDelete(w.id)} className="text-red-600 hover:underline text-xs">删除</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-xs text-gray-500 mt-3">
            <span>第 {page} / {totalPages} 页</span>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">上一页</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">下一页</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SensitiveWords
